import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { User, ArrowRight } from 'lucide-react';
import Seo, { breadcrumbJsonLd } from '../components/Seo.jsx';
import { BlogHero, PostGrid } from '../components/blog/BlogParts.jsx';
import { blogApi, imageUrl, PATHS } from '../lib/opinly';

const NAVY = '#1a3a5c';
const TEAL = '#3d94b8';

const Avatar = ({ image, name, size = 'h-16 w-16' }) => imageUrl(image?.fileKey)
  ? <img src={imageUrl(image.fileKey)} alt={image?.alt || name} className={`${size} rounded-full object-cover shadow-md`} />
  : <span className={`${size} rounded-full flex items-center justify-center shadow-md`} style={{ background: '#e6f2f7' }}><User className="h-1/2 w-1/2" style={{ color: TEAL }} /></span>;

export function BlogAuthors() {
  const [state, setState] = useState({ authors: [], loading: true, error: null });
  useEffect(() => { blogApi.authors().then((r) => setState({ authors: r.data || [], loading: false, error: null })).catch((error) => setState({ authors: [], loading: false, error })); }, []);
  return (
    <div>
      <Seo title="Autoren – Blog" description="Die Menschen hinter den AvantiTutor-Beiträgen." path={PATHS.authors} jsonLd={[breadcrumbJsonLd([{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }, { name: 'Autoren', path: PATHS.authors }])]} />
      <BlogHero eyebrow="Autoren" title="Wer hier schreibt" description="Tutoren und Lernexperten aus dem AvantiTutor-Team." crumbs={[{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }, { name: 'Autoren' }]} />
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {state.error ? <PostGrid posts={[]} error={state.error} onRetry={() => window.location.reload()} /> : state.loading ? <PostGrid posts={[]} loading /> : !state.authors.length ? <PostGrid posts={[]} emptyText="Noch keine Autoren hinterlegt." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="authors-grid">
              {state.authors.map((a) => (
                <Link key={a.slug} to={PATHS.author(a.slug)} data-testid={`author-card-${a.slug}`} className="card-soft p-7 flex flex-col items-start gap-4 hover:-translate-y-1 transition-transform">
                  <Avatar image={a.image} name={a.name} />
                  <div><h2 className="text-xl font-extrabold" style={{ color: NAVY }}>{a.name}</h2>{a.bio && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.bio}</p>}</div>
                  <span className="mt-auto text-sm font-semibold inline-flex items-center gap-1" style={{ color: '#2d7d9a' }}>{a.posts?.length || 0} {a.posts?.length === 1 ? 'Beitrag' : 'Beiträge'} <ArrowRight className="h-3.5 w-3.5" /></span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function BlogAuthor() {
  const { slug } = useParams();
  const [state, setState] = useState({ author: null, loading: true, error: null });
  useEffect(() => {
    setState({ author: null, loading: true, error: null });
    blogApi.author(slug).then((r) => setState({ author: r.data, loading: false, error: null })).catch((error) => setState({ author: null, loading: false, error }));
  }, [slug]);

  const a = state.author;
  const path = PATHS.author(slug);
  if (state.error?.status === 404) return (
    <div data-testid="author-not-found" className="max-w-xl mx-auto my-24 px-5 text-center">
      <Seo title="Autor nicht gefunden" noindex />
      <h1 className="text-3xl font-extrabold" style={{ color: NAVY }}>Autor nicht gefunden</h1>
      <Link to={PATHS.authors} className="inline-block mt-5 font-semibold hover:underline" style={{ color: '#2d7d9a' }}>Alle Autoren</Link>
    </div>
  );

  return (
    <div>
      <Seo title={a ? `${a.name} – Autor` : 'Autor'} description={a?.bio || `Beiträge von ${a?.name || ''}`} image={imageUrl(a?.image?.fileKey)} type="profile" path={path}
        jsonLd={a ? [{ '@context': 'https://schema.org', '@type': 'Person', name: a.name, description: a.bio || undefined, image: imageUrl(a.image?.fileKey) || undefined, url: `${window.location.origin}${path}` }, breadcrumbJsonLd([{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }, { name: 'Autoren', path: PATHS.authors }, { name: a.name, path }])] : []} />
      <BlogHero eyebrow="Autor" title={a?.name || '…'} description={a?.bio} crumbs={[{ name: 'Startseite', path: '/' }, { name: 'Blog', path: PATHS.index }, { name: 'Autoren', path: PATHS.authors }, { name: a?.name || '…' }]}>
        {a && <div className="mt-6"><Avatar image={a.image} name={a.name} size="h-24 w-24" /></div>}
      </BlogHero>
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold mb-8" style={{ color: NAVY }}>Beiträge{a ? ` von ${a.name}` : ''}</h2>
          <PostGrid posts={a?.posts || []} loading={state.loading} error={state.error} onRetry={() => window.location.reload()} emptyText="Noch keine Beiträge von diesem Autor." />
        </div>
      </section>
    </div>
  );
}
